import styled from "styled-components"
import { getCssSizeValue } from "@/utils/utils"
import Text from "@/components/common/Text"
import dynamic from "next/dynamic"
import React, { useMemo } from "react"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { useIsMobile } from "@/hooks/useDevice"

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		align-items: center;
		flex-direction: row;
		gap: ${getCssSizeValue(100, BREAKPOINTS.TABLET)};
		justify-content: space-between;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		gap: unset;
	}
`

const HeaderAndDescription = styled.div`
	display: flex;
	flex-direction: column;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		gap: ${getCssSizeValue(8, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		gap: ${getCssSizeValue(8, BREAKPOINTS.DESKTOP)};
	}

	.header {
		position: relative;
		font-weight: 900;

		&:before {
			--size: ${getCssSizeValue(8, BREAKPOINTS.MOBILE)};
			position: absolute;
			content: "";
			top: 50%;
			translate: 0 -50%;
			left: ${getCssSizeValue(-16)};
			width: var(--size);
			height: var(--size);
			background: ${({ theme }) => theme.colors.richBlack};

			@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
				--size: ${getCssSizeValue(8, BREAKPOINTS.TABLET)};
			}

			@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
				--size: ${getCssSizeValue(8)};
			}
		}
	}

	.description {
		font-weight: 500;
	}
`

const ACTION_VARIANT = {
	NEWSLETTER: "newsletter_modal",
	SHARE_BUTTONS: "share_buttons",
}

const ACTIONS = {
	[ACTION_VARIANT.NEWSLETTER]: dynamic(
		() => import("@/components/common/StructuredText/components/Blocks/BulletPointsWithActions/ActionsVariants/NewsletterModalAction"),
		{ ssr: false },
	),
	[ACTION_VARIANT.SHARE_BUTTONS]: dynamic(
		() => import("@/components/common/StructuredText/components/Blocks/BulletPointsWithActions/ActionsVariants/ShareButtons"),
		{ ssr: false },
	),
}

function getDataForBulletPoint(data) {
	const header = data?.header || ""
	const description = data?.description || ""
	const action = data?.action || null

	return {
		header,
		description,
		action,
	}
}

const MOBILE_EXCLUDED_ACTIONS = [ACTION_VARIANT.NEWSLETTER]

export default function SingleBulletPoint({ data }) {
	const isMobile = useIsMobile()
	const { header, description, action } = getDataForBulletPoint(data)
	const ActionComponent = useMemo(() => ACTIONS[action], [action])
	const shouldRender = !(isMobile && MOBILE_EXCLUDED_ACTIONS.includes(action))

	return (
		<>
			{shouldRender && (
				<Wrapper>
					<HeaderAndDescription>
						<Text className="typo-strawford typo-strawford-93 header">{header}</Text>
						<Text className="typo-strawford typo-strawford-94 description">{description}</Text>
					</HeaderAndDescription>
					<ActionComponent />
				</Wrapper>
			)}
		</>
	)
}
